import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import { Refresh as RefreshIcon, BugReport as BugIcon } from "@mui/icons-material";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch JavaScript errors in child components
 * and display a fallback UI instead of crashing the entire app
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Check if this is the known CraftJS delete error
    const isCraftJSDeleteError =
      error.message?.includes("Cannot read properties of undefined (reading 'children')") ||
      error.message?.includes("Cannot read property 'children' of undefined");

    if (isCraftJSDeleteError) {
      // Return hasError: false to prevent error UI from showing
      return { hasError: false, error: null };
    }

    // For other errors, show error UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is the known CraftJS delete error
    const isCraftJSDeleteError =
      error.message?.includes("Cannot read properties of undefined (reading 'children')") ||
      error.message?.includes("Cannot read property 'children' of undefined");

    if (isCraftJSDeleteError) {
      // Force a re-render to sync UI with state
      setTimeout(() => {
        this.forceUpdate();
      }, 10);
      return;
    }

    this.setState({ errorInfo });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log other errors to console in development
    console.error("❌ [ErrorBoundary] Caught an error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            height: "100%",
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 500,
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "error.light",
              bgcolor: "error.50",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "error.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <BugIcon sx={{ fontSize: 36, color: "error.main" }} />
            </Box>

            <Typography variant="h5" color="error.dark" fontWeight={600} gutterBottom>
              เกิดข้อผิดพลาด
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 3 }}>
              มีบางอย่างผิดพลาด กรุณาลองใหม่อีกครั้ง
            </Typography>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "grey.900",
                  borderRadius: 1,
                  textAlign: "left",
                  overflow: "auto",
                  maxHeight: 200,
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontSize: 12,
                    color: "error.light",
                    fontFamily: "monospace",
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack && (
                    <>
                      {"\n\nComponent Stack:"}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
                sx={{ borderRadius: 2 }}
              >
                โหลดใหม่
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={this.handleReset}
                sx={{ borderRadius: 2, borderColor: "grey.400" }}
              >
                ลองอีกครั้ง
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap a component with ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
